import { TransferItem } from 'antd/lib/transfer';
import React, { Key } from 'react';
import { GeneralSubPermission, Member } from './DeviceAdminModel';

export type CodeProperties = {
	E: string;
	N: string;
};

export type ChartData = {
	type: string;
	value: number;
	color: string;
};

export type GridDataItem = {
	Key: string;
	Value: string;
};

export type GridDataColumnItem = GridDataItem & {
	DisableSort: boolean;
	IsDateFormat: boolean;
	IsAuxColumn: boolean;
	DataIndex: string;
};

export type GridColumn = {
	Key: string;
	Header: string;
	Order: number;
	Width: number;
	Visible: boolean;
	IsDateFormat: boolean;
	IsAuxColumn: boolean;
	DataIndex: string;
};

export type GridItem = {
	ObjectId: number;
	Columns: GridDataColumnItem[];
	Data: GridDataItem[][];
	TotalItems?: number;
	PaginationSetting: PaginationSetting;
};

export type Tuple = {
	Item1: any;
	Item2: any;
};

export type TupleTyped<T, P> = {
	Item1: T;
	Item2: P;
};

export type TupleExtraItem = {
	Item3: any;
} & Tuple;

export type SelectOptions<T> = {
	key?: Key;
	value?: T;
	label: string;
	icon?: React.ReactNode;
	id?: string;
	separator?: boolean;
	disabled?: boolean;
	title?: string;
	nestedOptions?: SelectOptions<T>[];
	leaveTab?: boolean;
};

export type ButtonOption = {
	id?: string;
	disabled?: boolean;
	icon?: React.ReactNode;
	label: string;
	separator?: boolean;
	onClick?: () => void;
};

export enum ResponseStatusCode {
	Success = 0,
	DatabaseAccessError = 1,
	PermissionError = 2,
	InputError = 3,
	CommunicationError = 4,
	FailedValidation = 5,
	SystemError = 6,
	AdditionalInfoRequired = 7,
	ComponentLocked = 8,
	EventQueueExpired = 9,
	ServiceSessionExpired = 10,
}

export type ResponseObject = {
	ResponseStatusCode: ResponseStatusCode;
	AdditionalResponseInfo: string;
	ResponseErrorDescription: string;
	ErrorMessage: string;
	ResponseObjectId: number;
};

export type ResponseObjectEntity<T> = {
	Entity: T;
} & ResponseObject;

export type DefaultGridElement = {
	editable?: boolean;
	checked?: boolean;
};

export type BaseColumns = {
	key: React.Key;
};

export type NavigationListItem = {
	Id: React.Key;
	Name: string;
};

export type PermissionColumn = {
	Action: React.ReactNode;
	Name: string;
	SubItemID: number;
	Allowed: boolean;
} & BaseColumns;

export type DefaultColumns = {
	Name: string;
	Action: React.ReactNode;
} & BaseColumns;

export type OperatorKeyColumn = {
	Action: React.ReactNode;
	Id: number;
	Name: string;
	CreatedDate: Date | string;
} & BaseColumns;

export type RolesColumn = {
	Description?: string;
} & BaseColumns &
	DefaultColumns;

export type MemberColumn = {
	Action: React.ReactNode;
} & BaseColumns &
	Member;

export type GeneralSubPermissionColumn = {
	Action: React.ReactNode;
} & BaseColumns &
	GeneralSubPermission;

export type MemberPaginationSettings = {
	RoleId: number;
	IncludedInList: boolean;
} & PaginationSetting;

export type DefaultColumnsCredential = {
	Action: React.ReactNode;
	Description: string;
	CredentialTemplateId: number;
	IsDefault: boolean;
	ActivateDate: Date | string;
	ExpirationDate: Date | string;
	BadgeTemplate: string;
	Function: string;
	IDF: string;
	Tag: boolean;
	Alert: boolean;
	Disable: boolean;
} & BaseColumns;

export enum SortDirections {
	None,
	Ascend,
	Descend,
}

export type SorterConfig = {
	direction: SortDirections;
	field: string;
};

export type SorterConfigTyped<T> = {
	direction: SortDirections;
	field: T;
};

export type SearchSettings<T> = {
	Field: T;
	SearchValue: string;
};

export type ItemsPagination = {
	TotalItemsPaginated: number;
};

export type PaginationSetting = {
	PageNumber: number;
	PageSize: number;
	SortDirection?: SortDirections;
	SortField?: string;
	SearchedValue?: string;
	TotalItems?: number;
};

export type PaginationSettingTyped<T> = {
	PageNumber: number;
	PageSize: number;
	SortDirection: SortDirections;
	SortField: T;
	SearchSettings: SearchSettings<T> | SearchSettings<T>[];
};

export type DictionaryCustom<K, T> = {
	Key: K;
	Value: T;
};

export type ObjectTypeTransfer<T> = T & TransferItem;

export enum WithinTimes {
	none,
	anHour,
	twelveHours,
	aDay,
	twoDays,
	threeDays,
	aWeek,
	aMonth,
	aYear,
}

export type CriteriaField = {
	FieldId: number;
	UDFNumber?: number;
	DisplayName: string;
	Type: FieldDataType;
};

export type ResponseObjectColumns = {
	Columns: CriteriaField[];
} & ResponseObject;

export enum CriteriaOperatorFunction {
	none,
	is,
	isNot,
}

export enum CriteriaFunctionValue {
	none,
	equalTo,
	lessThan,
	greaterThan,
	between,
	startingWith,
	like,
	within,
	lessThanOrEqualTo,
	greaterThanOrEqualTo,
}

export enum SearchEqualToKey {
	none,
	searchByText,
	searchByPagination,
}

export enum UserProfileDropdownAction {
	about = 'about',
	help = 'help',
	settings = 'settings',
	logout = 'logout',
}

export type CriteriaInfo = CriteriaRequest & {
	key: number;
	fieldName: string;
	criteriaName: string;
	functionName: string;
	criteriaSetup: CriteriaSetup;
};

export type CriteriaSearchRequest = {
	CriteriaRequest: CriteriaRequest[];
};

export type CriteriaRequest = {
	FieldId: number;
	Function: CriteriaFunctionValue;
	FunctionOperator: CriteriaOperatorFunction;
	PrimaryInput: string;
	SecondaryInput: string;
	OrNextCriteria: boolean;
	Type: FieldDataType;
	IsUDF: boolean;
	IsEqualTo: boolean;
	UDFNumber: number;
};

export type CriteriaSetup = {
	Function: CriteriaFunctionValue;
	FunctionOperator: CriteriaOperatorFunction;
	Within?: WithinTimes;
};

export enum FieldDataType {
	String,
	Numeric,
	Boolean,
	DateTime,
}

export type PreviewBadgeResponse = ResponseObject & {
	Width: number;
	Height: number;
	IsLandscape: boolean;
	PhotoCredentialsBase64: string[];
	CredentialId: number;
	CredentialDescription: string;
	ShowBack: boolean;
};

export type SorterConfigEx = {
	order: string;
	columnKey: string;
};

export type SelectedDevicePage = {
	deviceId: number;
	pageNumber: number;
};

export type SelectedDeviceKeys = {
	Id: number;
	Name: string;
	IsMasterDoorGroup: boolean;
};

export type ErrorMessage = {
	message?: string;
	isInvalid?: boolean;
};

export enum CheckboxMenuOptions {
	SELECT_ALL = 0,
	CLEAR_ALL,
}

export enum KeyCodes {
	ENTER = 'Enter',
	ARROW_UP = 'ArrowUp',
	ARROW_DOWN = 'ArrowDown',
}

export type OptionsBuilderGeneric<T> = {
	id?: string;
	label: string;
	value?: T;
	options?: SelectOptions<T>[];
	disabled?: boolean;
	title?: string;
};

export type DigitracAvailableOptions = {
	isActionOptionsAvailable?: boolean;
	isControlOptionsAvailable?: boolean;
};

export type DigitracDisabledOptions = {
	isActionDisabled?: boolean;
	isControlDisabled?: boolean;
};

export type PortResetEncryptionActionOptions = {
	hasMoreThanOne: boolean;
	hasResetEncryption: boolean;
};

export enum DeviceActionTypes {
	DELETE = 0,
	RENAME,
	CONFIGURE,
	SET_AS_PRIMARY,
}

export type BeforeUploadPromise = {
	resolve: () => void;
	reject: () => void;
};

export enum PersistedObjectId {
	All = -1,
	None = 0,
	Console = 1,
	Administration = 2,
	AlarmViewer = 3,
	EventViewer = 4,
	Graphics = 5,
	Browser = 6,
	Status = 10,
	EnrollmentManager = 11,
	CCTV = 12,
	Draw = 13,
	DrawPackage = 14,
	WhosInside = 15,
	CustomizationManager = 16,
	Web = 17,
}

export type ImageResolution = {
	width: number;
	height: number;
	description?: string;
	callback?: (message: string) => void;
};

export enum ViewType {
	List = 0,
	Card = 1,
}

export type GraphicModeOption = {
	value: number;
	text: string;
	SelectedIcon: JSX.Element;
	UnSelectedIcon: JSX.Element;
	hasPermission: boolean;
};

export type RedlineConfigValues = {
	color: string;
	width: number;
	fillColor: string;
};

export type OperatorsDetail = { Id: number; Name: string };
